// A rental property's marketing files are Media rows (beleef-backend
// models/Media.js), not PrepareMarketing arrays. GET /admin/prepareMarketing/:id
// (and beleef-services-backend's GET /prepareMarketing/:id) send them as `mediaFiles`
// — { rows, events } — for a rental, and leave the arrays empty.
//
// The editor and contractor screens render from the arrays' fields, so this
// builds those fields from the rows — a read-only view; every write goes to
// beleef-backend's /admin/media endpoints (apis/media.js), addressed by
// Media _id. Kept in step with beleef-services-frontend's copy of this file.
//
// Ids: a RAW image keeps its Media _id. A web/print capture is numbered —
// the editor sorts, keys and allocates captures as numbers — by
// captureNumberOf(the web row's _id), which is stable across reloads. A new
// capture is given a fresh ObjectId up front (newCaptureId) and the server
// creates its row with that _id, so its number never changes either.

const isMediaId = (id) => typeof id === "string" && /^[0-9a-f]{24}$/i.test(id);

/**
 * A web photo's capture number: its ObjectId's timestamp and the low 20 bits
 * of its counter, which fit a JS integer and don't repeat within a property.
 */
export const captureNumberOf = (hex) => {
  const s = String(hex);
  return parseInt(s.slice(0, 8), 16) * 1048576 + (parseInt(s.slice(18, 24), 16) & 0xfffff);
};

const registry = new Map(); // capture number → ObjectId, for new captures

/** A new capture: a fresh ObjectId the server will create its row with. */
export function newCaptureId() {
  const bytes = new Uint8Array(8);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  const hex =
    Math.floor(Date.now() / 1000).toString(16).padStart(8, "0") +
    Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  const number = captureNumberOf(hex);
  registry.set(number, hex);
  return number;
}

export const usesMediaFiles = (marketing) => !!marketing?.mediaFiles;

const revisionsOf = (row) => (row?.revisions || []).filter(Boolean);
const activeUrl = (row) =>
  revisionsOf(row).find((r) => String(r._id) === String(row?.activeRevision))?.url ||
  row?.url ||
  "";

// Batch state for a job with no editor hand-off recorded (an agent's own
// Instant Marketing upload): its Event's status.
const BATCH_STATUS_OF_EVENT = {
  sent: "pending",
  scheduled: "pending",
  pending: "pending",
  inProgress: "inProgress",
};

const commentsOnSelections = (marketing, id) =>
  [
    ...((marketing.gallery || []).find((g) => String(g.id) === String(id))?.comments || []),
    ...((marketing.agentComments || []).find((g) => String(g.id) === String(id))?.comments ||
      []),
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

/**
 * The marketing document with its array fields built from `mediaFiles`, and
 * `__media` — the lookups apis/media.js needs to address the rows. Returns
 * the document unchanged for a sale.
 */
export function editorViewOf(marketing) {
  if (!usesMediaFiles(marketing)) return marketing;
  const { rows = [], events = [] } = marketing.mediaFiles;
  const eventById = new Map(events.map((e) => [String(e._id), e]));
  const of = (...roles) => rows.filter((r) => roles.includes(r.role));

  // RAW batches, one per job. An agent's own uploads belong to no job: they
  // are one batch with no eventId, reaching the editor only through
  // "send to human" (their status moves go to setJobStatus without one).
  const rawByEvent = new Map();
  for (const row of of("raw")) {
    const key = row.eventId ? String(row.eventId) : null;
    if (!rawByEvent.has(key)) rawByEvent.set(key, []);
    rawByEvent.get(key).push(row);
  }
  const rawPhotos = [...rawByEvent.entries()].map(([eventId, eventRows]) => {
    const event = (eventId && eventById.get(eventId)) || (eventId ? {} : { summary: "Agent uploads" });
    const batch = event.metadata?.rawBatch || {};
    return {
      eventId,
      status:
        batch.status ||
        BATCH_STATUS_OF_EVENT[event.status] ||
        event.status ||
        "pending",
      summary: batch.summary || event.summary || "",
      editCount: batch.editCount ?? 0,
      notes: batch.notes || "",
      uploadedBy: batch.uploadedBy || eventRows[0]?.uploadedBy,
      uploaderType: batch.uploaderType || eventRows[0]?.uploaderType,
      uploadedAt: batch.uploadedAt || eventRows[0]?.uploadedAt,
      createdAt: event.createdAt,
      images: eventRows.map((row) => ({
        id: String(row._id),
        mediaId: String(row._id),
        name: row.name,
        size: row.size,
        url: activeUrl(row),
        type: row.type ?? null,
        eventTypeKey: row.eventTypeKey ?? null,
        category: row.category ?? null,
        sourceMainImageId: row.sourceId ? String(row.sourceId) : null,
        revisions: revisionsOf(row),
        activeRevision: row.activeRevision ?? null,
      })),
    };
  });

  // Editor-review requests, kept on the raw rows.
  const rawPhotosComments = of("raw")
    .map((row) => {
      const reviews = (row.comments || []).filter((c) => c?.type === "editorReview");
      if (!reviews.length) return null;
      return {
        eventId: row.eventId ? String(row.eventId) : null,
        imageId: String(row._id),
        comments: reviews,
        paidRevisions: reviews
          .filter((c) => c.creditTransactionId)
          .map((c) => ({ revisionId: c.revisionId, creditTransactionId: c.creditTransactionId })),
      };
    })
    .filter(Boolean);

  // Web/print captures, numbered by the web row.
  const webIdByNumber = new Map();
  const webRows = of("web");
  const editedWebPhotos = webRows.map((row) => {
    const number = captureNumberOf(row._id);
    webIdByNumber.set(number, String(row._id));
    return {
      id: number,
      mediaId: String(row._id),
      eventId: row.eventId ? String(row.eventId) : undefined,
      name: row.name,
      size: row.size,
      url: activeUrl(row),
      category: row.category,
      status: row.status,
      webCropRect: row.webCropRect ?? null,
      rawPhotoImageId: row.sourceId ? String(row.sourceId) : null,
      revisions: revisionsOf(row),
      activeRevision: row.activeRevision ?? null,
      uploadedAt: row.uploadedAt,
      comments: commentsOnSelections(marketing, row._id),
    };
  });
  const editedPrintPhotos = of("print")
    .filter((row) => row.sourceId && webIdByNumber.has(captureNumberOf(row.sourceId)))
    .map((row) => ({
      id: captureNumberOf(row.sourceId),
      mediaId: String(row._id),
      webMediaId: String(row.sourceId),
      eventId: row.eventId ? String(row.eventId) : undefined,
      name: row.name,
      size: row.size,
      url: activeUrl(row),
      category: row.category,
      status: row.status,
      webCropRect: row.webCropRect ?? null,
      revisions: revisionsOf(row),
      activeRevision: row.activeRevision ?? null,
      uploadedAt: row.uploadedAt,
      comments: commentsOnSelections(marketing, row.sourceId),
    }));

  // Admin Extras: web photos an admin uploaded outside any job, numbered
  // e1, e2… in upload order as the arrays numbered them, each with its print.
  const s3KeyOf = (url) => (url || "").split("?")[0].split(".amazonaws.com/")[1] || null;
  const adminWebRows = webRows.filter((row) => !row.eventId && row.uploaderType === "Admin");
  const adminNumber = new Map(adminWebRows.map((row, i) => [String(row._id), `e${i + 1}`]));
  const adminWebPhotos = adminWebRows.map((row) => ({
    id: adminNumber.get(String(row._id)),
    mediaId: String(row._id),
    name: row.name,
    size: row.size,
    url: activeUrl(row),
    s3Key: s3KeyOf(activeUrl(row)),
  }));
  const adminPrintPhotos = of("print")
    .filter((row) => row.sourceId && adminNumber.has(String(row.sourceId)))
    .map((row) => ({
      id: adminNumber.get(String(row.sourceId)),
      mediaId: String(row._id),
      name: row.name,
      size: row.size,
      url: activeUrl(row),
      s3Key: s3KeyOf(activeUrl(row)),
    }));

  const extraPhotos = of("extra").map((row) => ({
    id: String(row._id),
    mediaId: String(row._id),
    eventId: row.eventId ? String(row.eventId) : undefined,
    name: row.name,
    size: row.size,
    url: activeUrl(row),
    type: row.type ?? null,
    bracketId: row.bracketId ?? null,
    eventTypeKey: row.eventTypeKey ?? null,
    uploaderType: row.uploaderType,
  }));

  const floorplan = of("floorplanWeb", "floorplanPrint").map((row) => ({
    id: row.role === "floorplanPrint" ? 2 : 1,
    mediaId: String(row._id),
    type: row.role === "floorplanPrint" ? "print" : "web",
    eventId: row.eventId ? String(row.eventId) : undefined,
    name: row.name,
    size: row.size,
    url: activeUrl(row),
    status: row.status,
    comments: row.comments || [],
    revisions: revisionsOf(row),
    activeRevision: row.activeRevision ?? null,
  }));

  const videoView = (row, index) => ({
    id:
      row.orientation === "portrait" ? 1 : row.orientation === "snippet" ? 3 : row.orientation ? 2 : index + 1,
    mediaId: String(row._id),
    eventId: row.eventId ? String(row.eventId) : undefined,
    name: row.name,
    size: row.size,
    url: activeUrl(row),
    orientation: row.orientation,
    status: row.status,
    comments: row.comments || [],
  });

  return {
    ...marketing,
    rawPhotos,
    rawPhotosComments,
    editedWebPhotos,
    editedPrintPhotos,
    extraPhotos,
    adminWebPhotos,
    adminPrintPhotos,
    floorplan,
    editedVideos: of("videoEdited").map(videoView),
    rawVideos: of("videoRaw").map(videoView),
    __media: {
      propertyId: String(marketing.propertyId?._id || marketing.propertyId),
      /** The web row a capture number names, if it has one yet. */
      webIdOf: (number) => webIdByNumber.get(Number(number)) || registry.get(Number(number)) || null,
      isSaved: (number) => webIdByNumber.has(Number(number)),
    },
  };
}

/** The same view for one job's rows, as the contractor's job screens get them (event.media). */
export function eventViewOf(event) {
  if (!Array.isArray(event?.media)) return event;
  const view = editorViewOf({
    propertyId: event.propertyId,
    gallery: [],
    agentComments: [],
    mediaFiles: { rows: event.media, events: [event] },
  });
  return {
    ...event,
    rawPhotos: view.rawPhotos.flatMap((batch) => batch.images),
    extraPhotos: view.extraPhotos,
    floorplan: view.floorplan,
    editedVideos: view.editedVideos,
    rawVideos: view.rawVideos,
    editedWebPhotos: view.editedWebPhotos,
    editedPrintPhotos: view.editedPrintPhotos,
    __media: view.__media,
  };
}

export { isMediaId };
